import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import placeholderImage from "../../assets/placeholder-image.png";
import pinchHand from "./pinch-hand/hand.png";
import pinchArrowUp from "./pinch-hand/arrow0.png";
import pinchArrowDown from "./pinch-hand/arrow1.png";
import "./FabricImageZoomPan.css";

type Props = {
  src: string;
  alt: string;
  className?: string;
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;

export function FabricImageZoomPan({ src, alt, className }: Props) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const scaleRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const gestureRef = useRef<
    | {
        mode: "pan";
        id: number;
        x: number;
        y: number;
        panX: number;
        panY: number;
      }
    | {
        mode: "pinch";
        dist: number;
        scale: number;
        panX: number;
        panY: number;
        midX: number;
        midY: number;
      }
    | null
  >(null);

  scaleRef.current = scale;
  panRef.current = pan;

  useEffect(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, [src]);

  const clampPan = useCallback((x: number, y: number, s: number) => {
    const m = 140 * (s - 1);
    return {
      x: Math.min(m, Math.max(-m, x)),
      y: Math.min(m, Math.max(-m, y)),
    };
  }, []);

  const onWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const zoomIn = e.deltaY < 0;
    const factor = zoomIn ? 1.09 : 1 / 1.09;
    setScale((prev) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * factor)),
    );
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  useEffect(() => {
    setPan((p) => {
      const next = clampPan(p.x, p.y, scale);
      if (next.x === p.x && next.y === p.y) return p;
      return next;
    });
  }, [scale, clampPan]);

  const onMouseDown = (e: React.MouseEvent) => {
    if (scale <= MIN_SCALE) return;
    e.preventDefault();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      setPan(clampPan(d.panX + dx, d.panY + dy, scale));
    };
    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, clampPan, scale]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const touchDistance = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    const touchMid = (a: Touch, b: Touch) => ({
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    });

    const applyScale = (nextScale: number, nextPan: { x: number; y: number }) => {
      const s = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const p = s <= MIN_SCALE ? { x: 0, y: 0 } : clampPan(nextPan.x, nextPan.y, s);
      scaleRef.current = s;
      panRef.current = p;
      setScale(s);
      setPan(p);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        const [a, b] = [e.touches[0], e.touches[1]];
        const m = touchMid(a, b);
        gestureRef.current = {
          mode: "pinch",
          dist: Math.max(1, touchDistance(a, b)),
          scale: scaleRef.current,
          panX: panRef.current.x,
          panY: panRef.current.y,
          midX: m.x,
          midY: m.y,
        };
        e.preventDefault();
        return;
      }
      if (scaleRef.current <= MIN_SCALE) return;
      const t = e.touches[0];
      if (!t) return;
      gestureRef.current = {
        mode: "pan",
        id: t.identifier,
        x: t.clientX,
        y: t.clientY,
        panX: panRef.current.x,
        panY: panRef.current.y,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const g = gestureRef.current;
      if (!g) return;
      if (g.mode === "pinch" && e.touches.length >= 2) {
        e.preventDefault();
        const [a, b] = [e.touches[0], e.touches[1]];
        const m = touchMid(a, b);
        const rawScale = g.scale * (touchDistance(a, b) / g.dist);
        const nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, rawScale));
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const k = nextScale / g.scale;
        applyScale(nextScale, {
          x: (g.midX - cx) * (1 - k) + g.panX * k + (m.x - g.midX),
          y: (g.midY - cy) * (1 - k) + g.panY * k + (m.y - g.midY),
        });
        return;
      }
      if (g.mode !== "pan" || scaleRef.current <= MIN_SCALE) return;
      const t = Array.from(e.touches).find((x) => x.identifier === g.id);
      if (!t) return;
      e.preventDefault();
      applyScale(scaleRef.current, {
        x: g.panX + (t.clientX - g.x),
        y: g.panY + (t.clientY - g.y),
      });
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length >= 2) return;
      if (e.touches.length === 1 && scaleRef.current > MIN_SCALE) {
        const t = e.touches[0];
        gestureRef.current = {
          mode: "pan",
          id: t.identifier,
          x: t.clientX,
          y: t.clientY,
          panX: panRef.current.x,
          panY: panRef.current.y,
        };
        return;
      }
      gestureRef.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [clampPan]);

  const onDoubleClick = () => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  };

  const rootClass = [
    "fabric-zoom",
    dragging ? "fabric-zoom--dragging" : "",
    scale > MIN_SCALE ? "fabric-zoom--zoomed" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={wrapRef}
      className={rootClass}
      onMouseDown={onMouseDown}
      onDoubleClick={onDoubleClick}
    >
      <div
        className="fabric-zoom__inner"
        style={{
          transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${scale})`,
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = placeholderImage;
          }}
        />
      </div>
      <div className="fabric-zoom__hint" aria-hidden="true">
        <svg className="fabric-zoom__hint-mouse" viewBox="0 0 48 72">
          <rect x="5" y="3" width="38" height="66" rx="19" fill="rgba(20,20,20,0.62)" stroke="#fff" strokeWidth="2" />
          <path d="M24 3v26" fill="none" stroke="#fff" strokeWidth="1.6" />
          <rect className="fabric-zoom__wheel" x="19.5" y="12" width="9" height="16" rx="4.5" />
        </svg>
        <svg className="fabric-zoom__hint-pinch" viewBox="0 0 86 100">
          <rect x="0.8" y="0.8" width="84.4" height="98.4" rx="16" fill="#fff" />
          <g transform="translate(52 16)">
            <g>
              <animateTransform
                attributeName="transform"
                type="scale"
                values="0.42;1;1;0.42"
                keyTimes="0;0.4;0.72;1"
                dur="1.8s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.42 0 0.2 1;0 0 1 1;0.4 0 0.58 1"
              />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" />
              <image href={pinchArrowUp} x="-52" y="-16" width="86" height="100" />
            </g>
          </g>
          <g transform="translate(14 61)">
            <g>
              <animateTransform
                attributeName="transform"
                type="scale"
                values="0.42;1;1;0.42"
                keyTimes="0;0.4;0.72;1"
                dur="1.8s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.42 0 0.2 1;0 0 1 1;0.4 0 0.58 1"
              />
              <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" />
              <image href={pinchArrowDown} x="-14" y="-61" width="86" height="100" />
            </g>
          </g>
          <image href={pinchHand} x="0" y="0" width="86" height="100" />
          <g fill="none" stroke="#666" strokeWidth="2.15">
            <circle cx="33.8" cy="31.2" r="5.7" opacity="0.95">
              <animate attributeName="cx" values="30.1;33.8;36.4;30.1" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" calcMode="spline" keySplines="0.42 0 0.2 1;0.3 0 0.7 1;0.4 0 0.58 1" />
              <animate attributeName="cy" values="36.9;31.2;27.8;36.9" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" calcMode="spline" keySplines="0.42 0 0.2 1;0.3 0 0.7 1;0.4 0 0.58 1" />
              <animate attributeName="opacity" values="0;0.95;0.95;0" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" />
            </circle>
            <circle cx="25" cy="44.5" r="5.8" opacity="0.95">
              <animate attributeName="cx" values="28.7;25;21.8;28.7" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" calcMode="spline" keySplines="0.42 0 0.2 1;0.3 0 0.7 1;0.4 0 0.58 1" />
              <animate attributeName="cy" values="38.9;44.5;48.6;38.9" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" calcMode="spline" keySplines="0.42 0 0.2 1;0.3 0 0.7 1;0.4 0 0.58 1" />
              <animate attributeName="opacity" values="0;0.95;0.95;0" keyTimes="0;0.4;0.72;1" dur="1.8s" repeatCount="indefinite" />
            </circle>
          </g>
        </svg>
      </div>
    </div>
  );
}
